import { lotkaVolterra2D } from '../Pages/Biological/simulations/lotkaVolterra';
import { fisher2D } from '../Pages/Biological/simulations/fisher';
import { fhn2D } from '../Pages/Biological/simulations/fitzHughNagumo';
import { hhSpikeTrain } from '../Pages/Biological/simulations/hodgkinHuxley';
import { segregationABM } from '../Pages/Biological/simulations/zoo/segregation';
import { epidemicGrid } from '../Pages/Biological/simulations/zoo/epidemic';
import { flockingBoids } from '../Pages/Biological/simulations/zoo/flocking';
import {
    renderTuringPreview,
    renderMarkovPreview,
    renderMobilityPreview,
    renderLogisticsPreview,
    renderAudioPreview,
    renderArcadePreview,
} from './docsProceduralPreviews';

const PREVIEW_SIZE = { width: 400, height: 220 };

export const DOCS_PREVIEW_SPECS = {
    'bio-turing': {
        ...PREVIEW_SIZE,
        render: renderTuringPreview,
    },
    'bio-lotka': {
        ...PREVIEW_SIZE,
        simulate: lotkaVolterra2D,
        params: { prey0: 10, predator0: 5, alpha: 1.1, beta: 0.4, delta: 0.1, gamma: 0.4, diffusion2d: 0.15 },
        steps: 80,
    },
    'bio-fisher': {
        ...PREVIEW_SIZE,
        simulate: fisher2D,
        params: { diffusion: 0.18, growth: 0.55, dt: 0.08 },
        steps: 60,
    },
    'bio-fhn': {
        ...PREVIEW_SIZE,
        simulate: fhn2D,
        params: { stimulus: 0.5, a: 0.7, b: 0.8, tau: 12.5, diffusion2d: 0.1 },
        steps: 100,
    },
    'bio-hh': {
        ...PREVIEW_SIZE,
        simulate: hhSpikeTrain,
        params: { current: 12, gNa: 120, gK: 36, gL: 0.3 },
        steps: 400,
    },
    'bio-segregation': {
        ...PREVIEW_SIZE,
        simulate: segregationABM,
        params: { density: 0.65, minorityShare: 0.35, similarityThreshold: 0.4, stepsPerFrame: 12 },
        steps: 35,
    },
    'bio-epidemic': {
        ...PREVIEW_SIZE,
        simulate: epidemicGrid,
        params: { initialInfected: 14, infectionRate: 0.38, recoveryRate: 0.07, stepsPerFrame: 4 },
        steps: 45,
    },
    'bio-flocking': {
        ...PREVIEW_SIZE,
        simulate: flockingBoids,
        params: { count: 100, perception: 48, separation: 1.4, alignment: 0.9, cohesion: 0.7, maxSpeed: 3.5 },
        steps: 90,
    },
    'robot-markov': {
        ...PREVIEW_SIZE,
        render: renderMarkovPreview,
    },
    'robot-mobility': {
        ...PREVIEW_SIZE,
        render: renderMobilityPreview,
    },
    'robot-eoq': {
        ...PREVIEW_SIZE,
        render: (ctx, w, h) => renderLogisticsPreview(ctx, w, h, 'eoq'),
    },
    'robot-transshipment': {
        ...PREVIEW_SIZE,
        render: (ctx, w, h) => renderLogisticsPreview(ctx, w, h, 'network'),
    },
    'robot-facility': {
        ...PREVIEW_SIZE,
        render: (ctx, w, h) => renderLogisticsPreview(ctx, w, h, 'network'),
    },
    'robot-cvrp': {
        ...PREVIEW_SIZE,
        render: (ctx, w, h) => renderLogisticsPreview(ctx, w, h, 'network'),
    },
    'robot-sde-routing': {
        ...PREVIEW_SIZE,
        render: (ctx, w, h) => renderLogisticsPreview(ctx, w, h, 'sde'),
    },
    'art-audio': {
        ...PREVIEW_SIZE,
        render: renderAudioPreview,
    },
    'arcade-snake': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'snake') },
    'arcade-sliding': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'grid') },
    'arcade-tictactoe': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'board') },
    'arcade-pong': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'grid') },
    'arcade-connect4': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'board') },
    'arcade-pole': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'grid') },
    'arcade-bandit': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'grid') },
    'arcade-blackjack': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'board') },
    'arcade-lights': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'grid') },
    'arcade-battleship': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'grid') },
    'arcade-numberlink': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'grid') },
    'arcade-minesweeper': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'grid') },
    'arcade-road': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'snake') },
    'arcade-pacman': { ...PREVIEW_SIZE, render: (ctx, w, h) => renderArcadePreview(ctx, w, h, 'snake') },
};

export function renderDocsPreview(postId) {
    const spec = DOCS_PREVIEW_SPECS[postId];
    if (!spec) return null;

    const canvas = document.createElement('canvas');
    canvas.width = spec.width;
    canvas.height = spec.height;
    const ctx = canvas.getContext('2d');

    if (spec.render) {
        spec.render(ctx, spec.width, spec.height);
        return canvas.toDataURL('image/png');
    }

    if (spec.simulate) {
        const state = spec.simulate.init(spec.width, spec.height, spec.params);
        for (let i = 0; i < spec.steps; i += 1) {
            spec.simulate.step(ctx, spec.width, spec.height, state, spec.params);
        }
        return canvas.toDataURL('image/png');
    }

    return null;
}
